/* nf-carousel-a11y.js — NO FUGAZI compatibility layer for the T4S/Flickity carousels.
   ---------------------------------------------------------------------------
   WHAT IT FIXES
   The vendor slider marks off-screen slides aria-hidden="true" but leaves their
   links keyboard-focusable. That is a WCAG failure (axe: aria-hidden-focus) and
   it is what holds the site at Accessibility 90 and Agentic Browsing 3/4:

     aria-hidden-focus        (Accessibility,     weight 7)
     agent-accessibility-tree (Agentic Browsing,  1 of 4)

   Both fail on the same rule and the same element:
     div.t4s-flicky-slider > div.flickityt4s-viewport
       > div.flickityt4s-slider > article.t4s-col-item

   HOW
   It mirrors the vendor's OWN aria-hidden state rather than re-deriving
   visibility: for any slide the vendor has marked aria-hidden="true", focusable
   descendants get tabindex="-1"; when the vendor clears aria-hidden, the
   original tabindex is restored exactly. It never sets or removes aria-hidden
   itself, and it never touches a slide the vendor considers visible.

   Mirroring is deliberate. Re-deriving visibility from geometry would be a
   second, competing source of truth that could disagree with the vendor mid
   transition; following the attribute cannot.

   WHY tabindex AND NOT inert
   `inert` also removes pointer events. The vendor's aria-hidden can cover a
   slide that is partially visible at a container edge, and inert would make such
   a card unclickable by mouse — a functional regression for a lead-generating
   card. tabindex="-1" removes keyboard focus only, which is exactly what the
   axe rule requires and nothing more.

   WHY A MutationObserver AND NOT ONLY THE 'select' EVENT
   The vendor emits select/settle/ready, but the attribute write and the event
   are not guaranteed to be in the same tick, and autoPlay (2000ms here) drives
   changes continuously. Observing the attribute reacts to the real state
   whatever caused it, and keeps working if the vendor changes its event names
   on a theme update. The events are still hooked as a cheap belt-and-braces.

   SCOPE
   .t4s-flicky-slider only. On the homepage that is three carousels, and it is a
   no-op on two of them (logo list, feature columns) because they contain no
   focusable descendants. It self-limits: nothing happens where aria-hidden and
   focusable content do not coexist.

   THEME-UPDATE RISK: low. It reads one attribute and writes tabindex. If a
   future vendor release fixes this itself, this file becomes a harmless no-op
   and can be deleted along with its <script> tag in layout/theme.liquid.

   TO REMOVE: delete this file and its one line in layout/theme.liquid.
*/
(function () {
  'use strict';

  var SLIDER = '.t4s-flicky-slider';
  var FOCUSABLE = 'a[href],area[href],button,input,select,textarea,iframe,object,embed,summary,audio[controls],video[controls],[contenteditable="true"],[tabindex]';
  var STORE = 'data-nf-ti';           // original tabindex, '' means "had none"
  var MARK = 'data-nf-a11y-bound';    // so a slider is only wired once

  function focusablesIn(cell) {
    var list = cell.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      // Already unfocusable by the author's own choice, and not ours to manage.
      if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') === '-1' && !el.hasAttribute(STORE)) continue;
      if (el.disabled) continue;
      out.push(el);
    }
    return out;
  }

  function suppress(cell) {
    var els = focusablesIn(cell);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.hasAttribute(STORE)) continue;                 // already suppressed
      el.setAttribute(STORE, el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
      el.setAttribute('tabindex', '-1');
    }
  }

  function restore(cell) {
    var els = cell.querySelectorAll('[' + STORE + ']');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var original = el.getAttribute(STORE);
      if (original === '') el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', original);
      el.removeAttribute(STORE);
    }
  }

  function syncCell(cell) {
    if (!cell || cell.nodeType !== 1) return;
    if (cell.getAttribute('aria-hidden') === 'true') suppress(cell);
    else restore(cell);
  }

  function cellsOf(slider) {
    var track = slider.querySelector('.flickityt4s-slider');
    return track ? track.children : [];
  }

  function syncAll(slider) {
    var cells = cellsOf(slider);
    for (var i = 0; i < cells.length; i++) syncCell(cells[i]);
  }

  function bind(slider) {
    if (slider.getAttribute(MARK)) return;

    // Observe the SLIDER, not the .flickityt4s-slider track.
    //
    // The vendor builds its viewport/track wrapper asynchronously and can
    // replace that node after init. Binding to the track raced it: on a slow
    // first paint the observer ended up watching a node the vendor had already
    // discarded, so nothing was ever suppressed. Watching the slider subtree
    // survives the track being rebuilt.
    //
    // Only mark the slider bound once an observer is actually attached, so a
    // later pass retries instead of skipping a slider that failed to wire.
    if (!window.MutationObserver) return;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes') syncCell(r.target);
        else if (r.type === 'childList' && r.addedNodes.length) syncAll(slider);
      }
    }).observe(slider, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
      childList: true
    });

    slider.setAttribute(MARK, '1');
    syncAll(slider);

    // Belt and braces: the vendor's own lifecycle events.
    try {
      var F = window.Flickityt4s;
      var inst = F && F.data ? F.data(slider) : null;
      if (inst && typeof inst.on === 'function') {
        var again = function () { syncAll(slider); };
        inst.on('ready', again);
        inst.on('select', again);
        inst.on('settle', again);
      }
    } catch (e) { /* vendor shape changed; the observer still covers us */ }
  }

  function bindAll() {
    var sliders = document.querySelectorAll(SLIDER);
    for (var i = 0; i < sliders.length; i++) {
      bind(sliders[i]);
      // Re-sync already-bound sliders too: the vendor rebuilds its cells on
      // breakpoint changes, and a rebuilt cell arrives with the vendor's
      // aria-hidden already set, which is not an attribute *change* we would
      // otherwise see.
      if (sliders[i].getAttribute(MARK)) syncAll(sliders[i]);
    }
  }

  function start() {
    bindAll();
    // Sliders are initialised lazily and by section re-render in the theme
    // editor, so watch for late arrivals too.
    if (window.MutationObserver) {
      new MutationObserver(function () { bindAll(); })
        .observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', bindAll);
    window.addEventListener('load', bindAll);

    // Cheap safety net for a slider that initialises after everything above.
    // Three passes, then stop; this is not a polling loop.
    var passes = 0;
    var t = setInterval(function () { bindAll(); if (++passes >= 3) clearInterval(t); }, 1200);

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(bindAll, 250);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
